/**
 * 칸 복사 = 눌러서 고르고 Ctrl+C(#250 — 「누르기만 해도 복사」를 바꿈).
 *
 * 화면마다 따로 들고 있던 「한 번 누름 = 복사」를 여기 하나로 모았다. 칸을 누르면 그 글자를 **복사 대기**로 잡아 칸에 테두리를
 * 그리고, Ctrl+C(맥은 ⌘C)를 누르면 그때 클립보드에 넣는다. 입력칸에 커서가 있거나 글자를 마우스로 긁어 둔 상태면
 * 브라우저 기본 복사를 그대로 둔다. 다른 곳을 누르거나 Esc 면 풀린다.
 * 리스너는 앱 전체에 한 벌(`installCellCopy` — App 에서 한 번).
 */
type Armed = { text: string; el: HTMLElement | null; mark: boolean; done?: (r: { ok: boolean; error?: string }) => void };

let armed: Armed | null = null;

export const COPY_HINT = '누르고 Ctrl+C 로 복사';

/**
 * #446 후속(10-30 정여진 「복사한 모든 곳에 [적요 복사: ~] 창이 똑같이」) — 화면 아래 알림 한 벌.
 * 출납일보·입금 내역이 쓰던 모양(「적요 복사: …」 · 2.5초)을 앱 전체 기본으로. 화면이 `done` 을 주면 그 화면 알림이 대신한다.
 */
let toastEl: HTMLDivElement | null = null;
let toastTimer = 0;
export function copyToast(message: string) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'pointer-events-none fixed bottom-6 left-1/2 z-[60] max-w-[90vw] -translate-x-1/2 truncate rounded-lg bg-ink px-4 py-2 text-sm text-white shadow-lg';
    document.body.appendChild(toastEl);
  }
  toastEl.textContent = message;
  toastEl.hidden = false;
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => { if (toastEl) toastEl.hidden = true; }, 2500);
}
export const copyMessage = (label: string, text: string, count = 1) =>
  count > 1 ? `${label} ${count}칸 복사` : `${label} 복사: ${text.length > 40 ? `${text.slice(0, 40)}…` : text}`;

/** 칸의 머리글 글자(정렬 화살표·둘째 줄 설명은 뺀다). colSpan 을 더해 자리를 맞춘다. 못 찾으면 「칸」. */
export function headerLabel(el: HTMLElement | null): string {
  const cell = el?.closest('td, th') as HTMLTableCellElement | null;
  const head = cell?.closest('table')?.tHead;
  if (!cell || !head || head.rows.length === 0) return '칸';
  let col = 0;
  for (let c = cell.previousElementSibling as HTMLTableCellElement | null; c; c = c.previousElementSibling as HTMLTableCellElement | null) col += c.colSpan || 1;
  let at = 0;
  const last = head.rows[head.rows.length - 1];
  for (const th of Array.from(last?.cells ?? [])) {
    const span = th.colSpan || 1;
    if (col < at + span) {
      const first = th.innerText.split(String.fromCharCode(10))[0] ?? '';
      return first.replace(/[↕↑↓▲▼]/g, '').trim() || '칸';
    }
    at += span;
  }
  return '칸';
}

/**
 * `text` 를 복사 대기로. `el` 은 테두리를 그릴 칸(같은 칸 안을 다시 눌러도 풀리지 않는다).
 * `done` 이 없으면 Ctrl+C 때 화면 아래에 「<label> 복사: …」(label 이 없으면 그 칸의 머리글).
 */
export function armCopy(text: string, el: HTMLElement | null, done?: Armed['done'], mark = true, label?: string) {
  disarmCopy();
  if (!text) return;
  done ??= (r) => copyToast(r.ok ? copyMessage(label ?? headerLabel(el), text) : `복사하지 못했습니다: ${r.error}`);
  armed = { text, el, mark, done };
  if (el && mark) el.classList.add('copy-armed');
}

export function disarmCopy() {
  if (armed?.el && armed.mark) armed.el.classList.remove('copy-armed');
  armed = null;
}

function typing(): boolean {
  const a = document.activeElement as HTMLElement | null;
  if (!a) return false;
  const tag = a.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || a.isContentEditable;
}

let installed = false;
export function installCellCopy() {
  if (installed) return;
  installed = true;
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { disarmCopy(); return; }
    // 한글 자판이면 e.key 가 「ㅊ」 — 자리(code)로 본다.
    if (!(e.ctrlKey || e.metaKey) || e.altKey || e.code !== 'KeyC' || !armed) return;
    if (typing()) return;
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && sel.toString().trim()) return; // 긁어 둔 글자 = 브라우저 복사
    const a = armed;
    // 숨은 화면(화면 탭)·지워진 칸이면 버린다.
    if (a.el && (!a.el.isConnected || a.el.offsetParent === null)) { disarmCopy(); return; }
    e.preventDefault();
    navigator.clipboard.writeText(a.text).then(
      () => a.done?.({ ok: true }),
      (err) => a.done?.({ ok: false, error: err instanceof Error ? err.message : String(err) }));
  });
  // 다른 곳을 누르면 풀린다(고른 칸 안·같은 칸 다시 누르기는 그대로 — 그 click 이 다시 잡는다).
  window.addEventListener('mousedown', (e) => {
    if (!armed) return;
    if (armed.el && armed.el.contains(e.target as Node)) return;
    disarmCopy();
  }, true);
  // #446(10-30 정여진 「모든 항목 복사 가능하게」) — 화면이 따로 잡지 않은 표 칸도 누르면 보이는 글자를 복사 대기로.
  // 화면 처리(React)가 먼저 돈 뒤라, 이 누름으로 이미 무언가 잡혔으면(복사 칸·끌어 고르기) 건드리지 않는다.
  window.addEventListener('click', (e) => {
    const t = e.target as HTMLElement | null;
    if (!t || t.closest('button,a,input,select,textarea,label,[data-nocopy]')) return;
    const td = t.closest('td');
    if (!td || !td.closest('.app-main, .app-modal')) return;
    if (armed?.el && (armed.el.contains(td) || td.contains(armed.el))) return;
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && sel.toString().trim()) return;
    armCopy(td.innerText.trim(), td);
  });
}
